import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { marked } from 'marked'
import './RulesTab.css'

const PromptBuilder: React.FC = () => {
  const [rulesData, setRulesData] = useState<any | null>(null)
  const [editablePrompt, setEditablePrompt] = useState<string>('')
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [previewLoading, setPreviewLoading] = useState<boolean>(false)
  const [previewText, setPreviewText] = useState<string | null>(null)
  const [previewVersion, setPreviewVersion] = useState<number | null>(null)
  const [saving, setSaving] = useState<boolean>(false)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    const fetch = async () => {
      try {
        const resp = await axios.get('/rules')
        setRulesData(resp.data)
        setEditablePrompt(resp.data.system_prompt_preamble || '')
      } catch (err) {
        console.warn('Failed to load rules for prompt builder', err)
      }
    }
    fetch()
  }, [])

  // Close preview modal on Escape
  useEffect(() => {
    if (!showPreview) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowPreview(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showPreview])

  const savePrompt = async () => {
    if (!rulesData) return
    setSaving(true)
    setMessage(null)
    try {
      const payload = { ...rulesData, system_prompt_preamble: editablePrompt }
      await axios.put('/rules', payload)
      setRulesData(payload)
      setMessage('Prompt saved.')
    } catch (err) {
      console.warn('Failed to save prompt', err)
      setMessage('Failed to save prompt')
    } finally {
      setSaving(false)
      setTimeout(() => setMessage(null), 3000)
    }
  }

  // Generate a preview by fetching current rules and posting to /rules/preview
  const handlePreviewClick = async () => {
    if (showPreview) {
      setShowPreview(false)
      return
    }

    setPreviewLoading(true)
    setMessage(null)

    try {
      const resp = await axios.get('/rules')
      const post = await axios.post('/rules/preview', resp.data)
      const prompt = post.data?.prompt ?? ''
      const version = post.data?.version ?? null
      setPreviewText(prompt)
      setPreviewVersion(version)
      setShowPreview(true)
    } catch (err) {
      console.warn('Failed to generate preview:', err)
      setMessage('Failed to generate preview.')
    } finally {
      setPreviewLoading(false)
    }
  }

  return (
    <div className="rules-container">
      <div className="rules-header">
        <h2>Agent Instructions</h2>
      </div>

      <div className="system-prompt-section">
        <div className="editor-section full-width">
          <h3>Edit System Prompt</h3>
          <textarea
            className="system-prompt-input"
            value={editablePrompt}
            onChange={(e) => setEditablePrompt(e.target.value)}
            rows={10}
          />

          <div className="edit-actions">
            <button className="btn btn-secondary" onClick={handlePreviewClick} disabled={previewLoading}>
              {previewLoading ? 'Loading...' : (showPreview ? 'Close Preview' : 'Preview')}
            </button>
            <button className="btn btn-primary" onClick={savePrompt} disabled={saving}>
              {saving ? 'Saving...' : 'Save Prompt'}
            </button>
          </div>

          {message && <div className="update-message">{message}</div>}
        </div>

        {/* Modal-based preview */}
        {showPreview && (
          <div className="preview-modal" role="dialog" aria-modal="true" onClick={() => setShowPreview(false)}>
            <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="preview-modal-header">
                <h3>Preview {previewVersion ? `(v${previewVersion})` : ''}</h3>
                <button className="modal-close" aria-label="Close preview" onClick={() => setShowPreview(false)}>×</button>
              </div>
              <div className="prompt-content preview modal-body markdown-preview">
                {previewLoading ? (
                  <p>Generating preview...</p>
                ) : previewText ? (
                  <div dangerouslySetInnerHTML={{ __html: marked.parse(previewText) as string }} />
                ) : (
                  <p>No preview available.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PromptBuilder
